import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { zodResolver } from "@hookform/resolvers/zod";
import { IClienteForm } from "../../../types/cliente";
import { z } from "zod";
import { useEffect, useState } from "react";
import { Ibge } from "../../../services/Ibge";
import { toast } from "react-toastify";
import { useContextSite } from "../../../context/Context";
import { ISelectOptions } from "../../../types/inputs";
import { ViaCep } from "../../../services/ViaCep";
import {
  maskCep,
  maskCnpj,
  maskCpf,
  maskPhone,
  removeDigitos,
} from "../../../utils/masks";
import { Cliente } from "../../../services/Cliente";

const enderecoSchema = z.object({
  bairro: z
    .string({ message: "Campo obrigatorio" })
    .min(1, "Campo obrigatorio"),
  cep: z.string({ message: "Campo obrigatorio" }).min(9, "Cep invalido"),
  cidade: z
    .string({ message: "Campo obrigatorio" })
    .min(1, "Campo obrigatorio"),
  complemento: z.string().optional().or(z.literal("")),
  logradouro: z
    .string({ message: "Campo obrigatorio" })
    .min(1, "Campo obrigatorio"),
  numero: z
    .string({ message: "Campo obrigatorio" })
    .min(1, "Campo obrigatorio"),
  uf: z.string({ message: "Campo obrigatorio" }).min(1, "Campo obrigatorio"),
});

const schema = z.object({
  cpfCnpj: z
    .string({ message: "Campo obrigatorio" })
    .min(14, "CPF/CNPJ invalido"),
  email: z
    .string({ message: "Campo obrigatorio" })
    .email("Email invalido")
    .optional()
    .or(z.literal("")),
  nome: z
    .string({ message: "Campo obrigatorio" })
    .min(1, "Por favor preencha o nome completo"),
  telefone: z
    .string({ message: "Campo obrigatorio" })
    .refine(
      (val) => [10, 11].includes(val.replace(/\D/g, "").length),
      { message: "Telefone invalido" }
    ),
  tipo: z.string().optional(),
  endereco: enderecoSchema,
});

export const useDispatcherCustomerRegister = () => {
  const navigate = useNavigate();
  const [ufOptions, setUfOptions] = useState<ISelectOptions[]>([]);
  const { setIsLoad } = useContextSite();
  const { uuidAgendamento } = useParams();
  const [cidadesOptions, setCidadesOptions] = useState<ISelectOptions[]>([]);
  const [cidadeTemp, setCidadeTemp] = useState("");

  const {
    handleSubmit,
    watch,
    setValue,
    register,
    control,
    formState: { errors },
  } = useForm<IClienteForm>({
    defaultValues: {
      cpfCnpj: "",
      email: "",
      nome: "",
      telefone: "",
      tipo: TipoClienteEnum.PARTICULAR,
      endereco: {
        bairro: "",
        cep: "",
        cidade: "",
        complemento: "",
        logradouro: "",
        numero: "",
        uf: "",
      },
    },
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((item) => ({
          value: item.sigla,
          label: item.sigla,
          element: item,
        }));

        setUfOptions(options);
      })
      .catch((erro) => toast.error("Erro ao requisitar as UFs"));
  }, []);

  function handleCep() {
    if (watch("endereco.cep").length === 9) {
      setIsLoad(true);
      setTimeout(() => {
        ViaCep.get(watch("endereco.cep"))
          .then(({ data }) => {
            setValue("endereco.logradouro", data.logradouro);
            setValue("endereco.bairro", data.bairro);
            setValue("endereco.uf", data.uf);
            setCidadeTemp(data.localidade);
          })
          .catch((erro) => toast.error("Cep não encontrado"))
          .finally(() => setIsLoad(false));
      }, 1000);
    }
  }

  useEffect(() => {
    if (watch("endereco.uf")) {
      setValue("endereco.cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("endereco.uf") })
        .then(({ data }) => {
          const options = data.map((item) => ({
            value: item.nome,
            label: item.nome,
            element: item,
          }));
          setCidadesOptions(options);
          setValue("endereco.cidade", cidadeTemp);
        })
        .then(() => {
          setCidadeTemp("");
        })
        .catch((erro) => toast.error("Erro ao requisitar a lista de cidades"));
    }
  }, [watch("endereco.uf")]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(watch("endereco.cep")));
  }, [watch("endereco.cep")]);

  useEffect(() => {
    const currentValue = watch("telefone");
    const newPhoneValue = maskPhone(currentValue);
    if (currentValue !== newPhoneValue) {
      setValue("telefone", newPhoneValue, { shouldValidate: true });
    }
  }, [watch("telefone"), setValue]);

  useEffect(() => {
    if (watch("cpfCnpj")?.length > 14) {
      setValue("cpfCnpj", maskCnpj(watch("cpfCnpj")));
      return;
    }
    setValue("cpfCnpj", maskCpf(watch("cpfCnpj")));
  }, [watch("cpfCnpj")]);

  function submteForm(data: IClienteForm) {
    const PAYLOAD: IClienteForm = {
      ...data,
      cpfCnpj: removeDigitos(data.cpfCnpj),
    };

    setIsLoad(true);

    Cliente.post(PAYLOAD)
      .then(() => {
        toast.success("Cliente cadastrado com sucesso!");
        setTimeout(() => {
          navigate(`/agendamento/${uuidAgendamento}/despachante-cliente`);
        }, 1500);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem),
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  return {
    navigate,
    handleSubmit,
    register,
    control,
    errors,
    ufOptions,
    cidadesOptions,
    handleCep,
    submteForm,
  };
};
